import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPersonalMfaMethod from 'lumen/components/settings/personal/mfa-method';
import type { MfaMethodType } from 'lumen/utils/constants/mfa-constants';

interface SettingsPersonalMfaTurnOffOrUpdateSignature {
  Args: {
    mfaActiveMethod: MfaMethodType;
    toggleMfaModal: () => void;
    toggleMfaTurnOffOrUpdateScreen: () => void;
    unenrollMfa: () => void;
  };
}

export default class SettingsPersonalMfaTurnOffOrUpdate extends Component<SettingsPersonalMfaTurnOffOrUpdateSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];

  @action
  async onTurnOffClicked(): Promise<void> {
    this.args.toggleMfaTurnOffOrUpdateScreen();
    this.args.toggleMfaModal();
    const confirmWasClicked = await this.alerting.showWarningAlert({
      title: this.intl.t('mfa.turn_off_mfa'),
      buttons: {
        cancel: {
          text: this.intl.t('mfa.no_keep_on'),
        },
        confirm: {
          text: this.intl.t('mfa.yes_turn_off'),
        },
      },
    });

    if (confirmWasClicked) {
      this.args.unenrollMfa();
    }
  }

  <template>
    <div data-test-id="mfa-turn-off-or-update">
      <p class="paragraph-300--regular mb-400">{{t "mfa.description"}}
        <a
          class="prism-link"
          href="https://help.mylighthouse.com/en/articles/4570883-guide-to-multi-factor-authentication-mfa"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{t "general.learn_more"}}
          <PrismIcon @color="inherit" @name="link-out" @size="small" /></a>
      </p>

      <div class="flex flex-row items-center justify-between">
        <p class="text-300--regular">{{t "mfa.enabled_state"}}
          <span class="text-300--bold">{{t "mfa.on"}}</span>
        </p>

        <PrismButton
          @buttonType="secondary"
          @label={{t "mfa.turn_off"}}
          @onClick={{this.onTurnOffClicked}}
        />
      </div>

      <div class="divider-line mt-300 mb-300"></div>
      <SettingsPersonalMfaMethod
        @mfaActiveMethod={{@mfaActiveMethod}}
        @updateMfaClicked={{@toggleMfaTurnOffOrUpdateScreen}}
      />
    </div>
  </template>
}
